'use client';

import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

/**
 * ScrollToTopButton — плавающая кнопка «Наверх» в правом нижнем углу.
 * Аналог лендинговой BackToPrevious, но на дизайн-токенах (работает в тёмной
 * теме) и без landing-специфичной логики «вернуться к блоку».
 *
 * Появляется после прокрутки вниз. На мобиле приподнята над нижней навигацией
 * (Navbar bottom nav, h-16), на десктопе — у нижнего края.
 */

const VISIBLE_FROM = 300; // px от top, начиная с которого показываем кнопку

export default function ScrollToTopButton() {
  const reduced = useReducedMotion() ?? false;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const update = () => setVisible(window.scrollY > VISIBLE_FROM);
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const onClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  }, [reduced]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={onClick}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-20 right-5 md:bottom-6 md:right-6 z-40 inline-flex items-center justify-center w-12 h-12 rounded-full bg-surface/95 hover:bg-field border border-line text-primary backdrop-blur-sm shadow-lg transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label="Наверх"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
